import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, Music, Heart } from 'lucide-react';
import EventCard from '../components/features/EventCard';
import { eventsService } from '../services/api-service';
import './Home.css';

const Home = () => {
  const [nextEvent, setNextEvent] = useState(null);
  const [pastEvents, setPastEvents] = useState([]);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const next = await eventsService.getNextEvent();
      const past = await eventsService.getPastEvents();
      
      setNextEvent(next);
      setPastEvents(past.slice(0, 3));
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="home-hero">
        <div className="container">
          <div className="home-hero-content">
            <img 
              src="/Logo.jpg" 
              alt="Back to People" 
              className="home-hero-logo"
            />
            <h1 className="home-hero-title">
              Back to People
            </h1>
            <p className="home-hero-subtitle">
              A Reunion of Humans
            </p>
            <p className="home-hero-description">
              Where you can be yourself, express your emotions, connect with art, 
              music, and the vibe. Connection through authentic community celebrations.
            </p>
            <div className="home-hero-actions">
              <Link to="/events" className="btn btn-orange">
                View Events
                <ArrowRight size={20} />
              </Link>
              <Link to="/subscribe" className="btn btn-outline">
                Subscribe to Parties
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="home-philosophy section">
        <div className="container">
          <div className="home-philosophy-content">
            <h2>Our Philosophy</h2>
            <blockquote className="home-quote">
              "We don't organize these parties to clean up after other people. 
              We do so to create connection between people through mutual respect. 
              If we can't respect the earth, how can we respect each other?"
            </blockquote>
            <div className="home-values">
              <div className="home-value">
                <Heart className="home-value-icon" />
                <h3>Authentic Connection</h3>
                <p>Be yourself, express emotions, connect genuinely with art, music, and people</p>
              </div>
              <div className="home-value">
                <Users className="home-value-icon" />
                <h3>Community Respect</h3>
                <p>Quality over quantity - building relationships with respectful people</p>
              </div>
              <div className="home-value">
                <Music className="home-value-icon" />
                <h3>Art & Expression</h3>
                <p>Celebrating local artists giving back to the community through music and art</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Event */}
      {nextEvent && (
        <section className="home-next-event section bg-black text-white">
          <div className="container">
            <div className="home-section-header">
              <Calendar size={32} />
              <h2>Next Event</h2>
            </div>
            <div className="home-next-event-content">
              <EventCard event={nextEvent} showArtists={true} />
            </div>
          </div>
        </section>
      )}

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <section className="home-past-events section">
          <div className="container">
            <div className="home-section-header">
              <h2>Previous Celebrations</h2>
              <Link to="/events" className="home-section-link">
                View All Events
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="home-events-grid">
              {pastEvents.map((event) => (
                <EventCard key={event.id} event={event} showArtists={false} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="home-cta section bg-gray">
        <div className="container">
          <div className="home-cta-content">
            <h2>Join Our Community</h2>
            <p>
              Subscribe to receive party invitations and stay connected with the B2P family. 
              Let's party every night - responsibly.
            </p>
            <div className="home-cta-actions">
              <Link to="/subscribe" className="btn btn-orange">
                Subscribe Now
              </Link>
              <Link to="/about" className="btn btn-outline">
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Rules Preview */}
      <section className="home-rules section">
        <div className="container">
          <h2 className="text-center">Rules of Respect</h2>
          <div className="home-rules-grid">
            <div className="home-rule-card">
              <h3>Leave No Trace</h3>
              <p>Bring a rubbish bag, always bring your bag of love, take your rubbish with you</p>
            </div>
            <div className="home-rule-card">
              <h3>Respect the Earth</h3>
              <p>No littering, pack in pack out, leave it better than you found it</p>
            </div>
            <div className="home-rule-card">
              <h3>Be Authentic</h3>
              <p>Don't try to be cool, just be cool and come party</p>
            </div>
            <div className="home-rule-card">
              <h3>Keep the Secret</h3>
              <p>The longer we keep places secret, the longer we can party every night</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

